/* @jsx jsx */
import { css, jsx } from '@emotion/react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PageNavigationContext from './PageNavigationContext';

import { ColorName, Colors } from '../../../theme';
import { Icon } from '../../display/Icon';
import { Text } from '../../display/Text';
import { TooltipWrapper } from '../../display/TooltipWrapper';
import { Spacer } from '../../layout/Spacer';

import NestedSecondRow from './NestedSecondRow';
import PageNavigationBadge from './PageNavigationBadge';
import PageNavigationRow from './PageNavigationRow';
import type { ContentProps, OnSelectType, PageNavigationItem } from './types';
import checkIsTruncated from './utils/checkIsTruncated';

const Styles = {
  list: css`
    list-style: none;
    margin: 0;
    padding: 0;
  `,
  textEllipsis: (textColor: ColorName) => css`
    color: ${Colors[textColor]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  // The width value for textWrap is 90% of the row width, less the width
  // of the space available for the Badge count (32px)
  textWrap: css`
    align-items: center;
    display: flex;
    flex-direction: row;
    width: calc(90% - var(--zds-size-32));
  `,
  rowContainer: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: var(--zds-space-8) var(--zds-space-24) var(--zds-space-8)
      var(--zds-space-8);
    position: relative;
  `,
  iconContainer: [
    css`
      display: inline-block;
      left: 0px;
      padding: var(--zds-space-8);
      position: absolute;
      &:focus-visible {
        outline-color: ${Colors.UiPrimaryStrongest};
      }
    `,
  ],
  iconTextSpacing: css`
    padding-left: var(--zds-space-20);
  `,
};

/**
 * NestedFirstRow is a row renderer component that renders its item
 * and any children, if provided, as a nested list via the NestedSecondRow
 * component.
 */
export default function NestedFirstRow({
  isExpanded,
  item,
  onSelect,
}: {
  item: PageNavigationItem;
  isExpanded: boolean;
  onSelect?: OnSelectType;
}) {
  const { toggleExpand } = useContext(PageNavigationContext);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLElement>(null);

  const nestedListRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    // React doesn't support the `until-found` value for the `hidden` attribute, so we set it directly on the ul with a ref.
    if (nestedListRef.current) {
      if (isExpanded) {
        nestedListRef.current.removeAttribute('hidden');
      } else {
        nestedListRef.current.setAttribute('hidden', 'until-found');
      }
    }
  }, [isExpanded]);

  useEffect(() => {
    setIsTruncated(checkIsTruncated(textRef));
  }, []);

  const onToggleSelect = () => {
    toggleExpand(item.id);
    return onSelect;
  };

  const InnerContent = ({ hover, selected }: ContentProps) => {
    const textColor: ColorName =
      hover || selected ? 'UiPrimaryStrongest' : 'GrayWarm10';

    return (
      <div
        css={Styles.rowContainer}
        data-testid="pagenavigation-nestedfirstrow"
        data-zds
      >
        <span css={Styles.textWrap}>
          {item.children ? (
            <div
              css={Styles.iconContainer}
              data-testid="pagenavigation-nestedfirstrow-icon"
              data-zds
            >
              <Icon
                color="GrayWarm6"
                name={isExpanded ? 'arrowSmallUp' : 'arrowSmallDown'}
                size={20}
              />
            </div>
          ) : (
            <Spacer width={20} />
          )}
          <span
            css={[
              Styles.textEllipsis(textColor),
              item.children && Styles.iconTextSpacing,
            ]}
            data-testid="pagenavigation-nestedfirstrow-text"
            ref={textRef}
            data-zds
          >
            <Text color={textColor} margin="0 0 0 8px" type="Body3">
              {item.title}
            </Text>
          </span>
        </span>
        {item.count && (
          <PageNavigationBadge variant="neutral">
            {item.count.toString()}
          </PageNavigationBadge>
        )}
      </div>
    );
  };

  const Content = ({ hover, selected }: ContentProps) =>
    isTruncated ? (
      <TooltipWrapper
        allowMultilineTooltip={true}
        content={item.title}
        isBlock={true}
        position="northeast"
      >
        {() => <InnerContent hover={hover} selected={selected} />}
      </TooltipWrapper>
    ) : (
      <InnerContent hover={hover} selected={selected} />
    );

  const NestedList = useMemo(
    () =>
      item.children
        ? () => (
            <ul
              css={Styles.list}
              data-zds
              hidden={!isExpanded}
              ref={nestedListRef}
            >
              {item.children?.map((i) => (
                <NestedSecondRow item={i} key={i.id} onSelect={onSelect} />
              ))}
            </ul>
          )
        : undefined,
    [item, isExpanded, onSelect]
  );

  return (
    <PageNavigationRow
      content={Content}
      isSelectable={false}
      item={item}
      nestedList={NestedList}
      onSelect={item.children ? onToggleSelect : onSelect}
    />
  );
}
